import { Link } from 'react-router-dom';
import {
  ClipboardCheck,
  ClipboardList,
  Info,
  MessageSquare,
  Pencil,
} from 'lucide-react';
import type { PersonApi } from '../../hooks/usePeople';
import type { PersonMobilisation } from '../../hooks/usePeopleMemberships';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { FlagsButton } from '../person/FlagsButton';

/**
 * The people admin table, extracted from PeopleAdmin so the route file
 * stays under the size cap. Pure presentation: filtering, pagination,
 * and modal state live in the parent.
 *
 * The four quick actions are icon-only (tooltip + aria-label carry the
 * meaning); labelled buttons made the row wider than the page container
 * and pushed Edit off-screen.
 */
export function PeopleTable({
  people,
  memberships,
  membershipsLoading,
  onNotes,
  onTasks,
  onPreflight,
  onInfo,
}: {
  people: PersonApi[];
  memberships: Record<string, PersonMobilisation[]> | undefined;
  membershipsLoading: boolean;
  onNotes: (p: PersonApi) => void;
  onTasks: (p: PersonApi) => void;
  onPreflight: (p: PersonApi) => void;
  onInfo: (p: PersonApi) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-muted">
          <tr>
            <th className="px-4 py-2.5">Name</th>
            <th className="px-4 py-2.5">Email</th>
            <th className="px-4 py-2.5">Position</th>
            <th className="px-4 py-2.5">Contract</th>
            <th className="px-4 py-2.5">Mobilisation</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr
              key={p.id}
              className="border-t border-line/10 transition-colors hover:bg-surface-2/50"
            >
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2 font-medium">
                  <span className="whitespace-nowrap">{p.full_name}</span>
                  {p.archived ? (
                    <span className="whitespace-nowrap rounded-token-sm bg-warning-soft/15 px-1.5 py-0.5 text-xs font-medium text-warning-soft-fg">
                      Archived
                    </span>
                  ) : null}
                  <FlagsButton
                    personId={p.id}
                    personName={p.full_name}
                    count={p.active_flags ?? 0}
                  />
                </div>
              </td>
              <td className="px-4 py-2.5 text-xs text-fg-muted">{p.email}</td>
              <td className="px-4 py-2.5 text-xs text-fg-muted">
                {p.position ?? '—'}
              </td>
              <td className="px-4 py-2.5 text-xs text-fg-muted">
                {p.contract ?? '—'}
              </td>
              <td className="px-4 py-2.5">
                {membershipsLoading ? (
                  <span className="text-xs text-fg-dim">…</span>
                ) : (memberships?.[p.id] ?? []).length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {(memberships?.[p.id] ?? []).map((m) => (
                      <Pill
                        key={m.id}
                        tone="accent"
                        size="sm"
                        className="max-w-[180px]"
                      >
                        <span className="min-w-0 truncate" title={m.name}>
                          {m.name}
                        </span>
                      </Pill>
                    ))}
                  </div>
                ) : (
                  <Pill tone="warning" size="sm">none</Pill>
                )}
              </td>
              <td className="px-4 py-2.5 text-right">
                <div className="inline-flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<MessageSquare className="h-3.5 w-3.5" />}
                    onClick={() => onNotes(p)}
                    aria-label={`Notes for ${p.full_name}`}
                    title="Notes"
                    className="text-info-soft-fg hover:bg-info-soft/15"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<ClipboardList className="h-3.5 w-3.5" />}
                    onClick={() => onTasks(p)}
                    aria-label={`Tasks for ${p.full_name}`}
                    title="Tasks"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<ClipboardCheck className="h-3.5 w-3.5" />}
                    onClick={() => onPreflight(p)}
                    aria-label={`Pre-employment checklist for ${p.full_name}`}
                    title="Pre-Employment Checklist"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<Info className="h-3.5 w-3.5" />}
                    onClick={() => onInfo(p)}
                    aria-label={`View ${p.full_name}`}
                    title="Quick view"
                  />
                  <Link to={`/admin/people/${p.id}`}>
                    <Button
                      variant="secondary"
                      size="sm"
                      iconLeft={<Pencil className="h-3.5 w-3.5" />}
                    >
                      Edit
                    </Button>
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
